import React, { useEffect, useState } from "react";
import { updateMemberName, updateName } from "../../../../Utils/SupabaseServices/UserDatabase";
import { Button, Spin } from "antd";
import HelperText from "../../../../components/HelperText/HelperText";
import { useAuth } from "../../../../hooks/useAuth";
import { supabase } from "../../../../config/supabaseClient";

const Name = (props: any) => {
  const { username, userData, setIsName, title } = props;
  const { isMember, session, isAdmin } = useAuth();

  // Determine what name to show based on context
  const getInitialName = () => {
    if (!isAdmin && isMember && session?.user?.email !== userData?.email) {
      if (title === "Display Name") {
        // For members viewing Display Name, show creator's name
        return userData.name || "";
      } else {
        // For members viewing their own Name, show member's name from session
        return (isMember && session?.user?.email !== userData?.email) ? userData?.team_members?.find((member: any) => member?.email === session?.user?.email)?.name || "" : session?.user?.user_metadata?.name || "";
      }
    }
    // For creators, show their own name regardless of title
    return userData.name || "";
  };

  const [name, setName] = useState(getInitialName());
  const [originalName, setOriginalName] = useState(getInitialName()); // Keep track of original name
  const [editName, setEditName] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [isNameChanged, setIsNameChanged] = useState(false);
  const [nameError, setNameError] = useState("");

  // Update name when user type or session changes
  useEffect(() => {
    const newName = getInitialName();
    setName(newName);
    setOriginalName(newName);
  }, [isMember, session, userData]);

  const showModal = () => {
    setIsModalOpen(true);
  };

  const handleOk = () => {
    setIsModalOpen(false);
  };

  function handleUpdateValue(e: any, option: any) {
    if (option === "name") {
      if (e.target.value === "") {
        setName(e.target.value);
        setNameError("Name cannot be empty");
      } else {
        setName(e.target.value);
        setNameError("");
      }
    }
  }

  const doneEdit = async (option: any) => {
    if (option === "name") {
      setIsNameChanged(true);
      if (name === "" || name === "null") {
        setModalTitle("Enter Name!");
        showModal();
        return;
      }

      // Determine which user/name to update based on context
      if (!isAdmin && isMember && session?.user?.email !== userData?.email && title !== "Display Name") {
        // Member updating their own name - update auth user metadata
        try {
          const { error } = await supabase.auth.updateUser({
            data: { name: name }
          });
          if (!error) {
            setIsName(true);
          }
          await updateMemberName(userData?.user_id, session?.user?.email, name);
          props.refetch();
        } catch (error) {
          console.error("Error updating member name:", error);
        }
      } else {
        // Creator updating their name OR member viewing display name (read-only)
        if (!isAdmin && isMember && title === "Display Name") {
          // Don't allow editing display name for members
          return;
        }
        await updateName(username, name);
        setIsName(true);
        if (props.userData && !isMember) {
          props.userData.name = name;
        }
       await props.refetch();
      }
      setInterval(() => {
        setIsNameChanged(false);
      }, 1000);
    }
  };

  // Don't allow editing display name for members
  const isEditable = !(!isAdmin && isMember && title === "Display Name");

  const getHelperMessage = () => {
    if (!isAdmin && isMember && title === "Display Name") {
      return "This is how the creator's name appears on the page.";
    }
    return "How your name will be shown on your page.";
  };

  return (
    <div className="account-tile">
      <div className="account-tile-header">
        <div className="account-tile-title">
          <p>{title}</p>
          <HelperText message={getHelperMessage()} />
        </div>
      </div>
      <div className="account-tile-value">
        {!editName ? (
          <div style={{ alignItems: 'center', gap: "10px" }}>
            <div style={{ margin: '0.25rem 0 0.5rem' }}>{isNameChanged ? <Spin /> : name || ""}</div>
            {isEditable && (
              <Button
                className="accountButton"
                onClick={() => {
                  setEditName(true);
                  setOriginalName(name); // Store the current name before editing
                }}
                style={{
                  width: '100%',
                  padding: '0.5rem 1rem'
                }}
              >
                Change
              </Button>
            )}
          </div>
        ) : (
          <div>
            <input
              type="text"
              style={{ 
                width: '100%', 
                padding: '0.5rem', 
                borderRadius: '6px', 
                border: '1px solid #e5e7eb',
                marginBottom: '0.75rem'
              }}
              onChange={(e: any) => handleUpdateValue(e, "name")}
              value={name}
            />
            <div className="account-button-group">
              <Button
                className="accountButton"
                onClick={() => {
                  setEditName(false);
                  doneEdit("name");
                }}
                disabled={nameError === "Name cannot be empty"}
              >
                Save
              </Button>
              <Button
                className="cancelButton"
                onClick={() => {
                  setEditName(false);
                  setName(originalName); // Revert to the original name on cancel
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}
      </div>
      {nameError && <p style={{ color: '#ef4444', fontSize: '0.875rem', marginTop: '0.5rem' }}>{nameError}</p>}
    </div>
  );
};

export default Name;
